import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	input,
	output,
	signal,
	untracked,
} from '@angular/core';
import { FormInstance } from '@models/global-forms';
import { MatterPackageContent } from '@models/matter-package';
import {
	DocumentCategory,
	MatterWordDocument,
} from '@models/matter-word-documents';
import { dispatch, select } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from 'src/app/shared/services/analytics/analytics.service';
import { MatterFormService } from 'src/app/shared/services/matter-form/matter-form.service';
import {
	RemoveContent,
	ReorderContent,
	SyncPackageWithVirtual,
} from 'src/app/shared/state/matter/matter-packages/matter-packages.actions';
import { MatterWordDocumentWithMetadata } from 'src/app/shared/state/matter/matter-word-documents/matter-word-documents.selectors';
import { SelectedMatter } from 'src/app/shared/state/matter/matter.selectors';
import {
	AnalyticsContext,
	DocListItemComponent,
} from 'src/app/ui/doc-list-item/doc-list-item.component';
import { DropdownItem } from 'src/app/ui/dropdown/dropdown.component';
import {
	EmptyComponent,
	UI_EMPTY_PRESET,
} from 'src/app/ui/empty/empty.component';
import { MatterPackagesSelectors } from '../../matter-packages.selectors';
import { TestHookDirective } from '@core/ui';
import { TestHooks } from './matter-package-document-list.hooks';

export interface DocumentToRenderPayload {
	docxPreviewDocId: string | null;
	form: FormInstance | null;
	uploadedDocPreviewSourcePath: string | null;
}

@Component({
	selector: 'app-matter-package-document-list',
	templateUrl: './matter-package-document-list.component.html',
	imports: [
		CommonModule,
		DocListItemComponent,
		EmptyComponent,
		DragDropModule,
		TestHookDirective,
	],
})
export class MatterPackageDocumentListComponent {
	testHooks = TestHooks;
	readonly UI_EMPTY_PRESET = UI_EMPTY_PRESET;
	readonly ANALYTIC_CONTEXT = AnalyticsContext;
	private getMatterPackageById = select(
		MatterPackagesSelectors.getMatterPackageById,
	);
	private SyncPackageWithVirtual = dispatch(SyncPackageWithVirtual);
	private ReorderContent = dispatch(ReorderContent);
	private RemoveContent = dispatch(RemoveContent);
	private matterFormService = inject(MatterFormService);
	private analytics = inject(AnalyticsService);
	private allPackages = select(
		MatterPackagesSelectors.getAllPackagesWithStatusesAndDocuments,
	);
	public documents_ = computed<MatterWordDocumentWithMetadata[]>(
		() => this.getMatterPackageById()(this.packageId_())?.documents || [],
	);
	public withinEnvelopView_ = input<boolean>(false);
	public packageId_ = input<string>();
	public selectedDocumentIds_ = signal<string[]>([]);
	public dropdownItems_ = computed(() => {
		return this.documents_()?.reduce(
			(acc, item) => {
				acc[item.id] = [
					{
						id: 'remove',
						title: {
							label: 'Remove',
							icon: 'minus',
						},
						action: () => {
							this.analytics.logEvent(
								AnalyticsEvent.MATTER_BUILDER_DOCUMENT_KEBAB_REMOVE,
							);
							this.onDocumentRemove(item.document);
						},
					},
				];
				return acc;
			},
			{} as Record<string, DropdownItem[]>,
		);
	});
	public documentToRender_ = output<DocumentToRenderPayload>();
	public matterSource_ = select(SelectedMatter.matterSource);

	constructor() {
		effect(() => {
			const packageId = this.packageId_();
			if (!packageId) return;

			const allPackagesData = this.allPackages();
			const { matterPackages, allVirtualPackages } = allPackagesData;

			const matterPkg = matterPackages.find((pkg) => pkg.id === packageId);
			if (!matterPkg || !matterPkg.packageHash) return;

			const virtualPkg = allVirtualPackages.find(
				(pkg) => pkg.packageHash === matterPkg.packageHash,
			);
			if (!virtualPkg) return;

			this.SyncPackageWithVirtual({
				matterPackageId: packageId,
				virtualPackageHash: virtualPkg.packageHash,
				virtualContents: virtualPkg.contents,
				virtualDocuments: virtualPkg.documents.map((doc) => ({
					...doc,
					id: doc.document.id,
				})),
			});
		});

		effect(() => {
			const documents = this.documents_();

			let selectedIdInit =
				localStorage.getItem('matter-packages-selected-doc-id') ??
				documents?.[0]?.id;

			const currentlySelectedDocId = untracked(
				() => this.selectedDocumentIds_()?.[0],
			);

			if (
				currentlySelectedDocId &&
				documents.some((d) => d.id === currentlySelectedDocId)
			) {
				selectedIdInit = currentlySelectedDocId;
			}

			if (selectedIdInit || documents.length > 0) {
				const doc = selectedIdInit
					? documents.find((d) => d.id === selectedIdInit)
					: documents[0];
				this.onDocumentPreview(doc);
				localStorage.removeItem('matter-packages-selected-doc-id');
			}
		});
	}

	drop(event: CdkDragDrop<MatterPackageContent[]>) {
		this.ReorderContent({
			id: this.packageId_(),
			contentId: this.documents_()[event.previousIndex].id,
			newIndex: event.currentIndex,
		});
	}

	public onDocumentPreview(doc: MatterWordDocumentWithMetadata) {
		if (!doc) {
			this.documentToRender_.emit({
				form: null,
				docxPreviewDocId: null,
				uploadedDocPreviewSourcePath: null,
			});
			return;
		} else if (
			doc.document.categories?.includes(DocumentCategory.SYNC_FORMS) ||
			doc.document.categories?.includes(DocumentCategory.MATTER_FORMS)
		) {
			this.setForm(doc.id);
		} else if (doc.document.categories?.includes(DocumentCategory.UPLOAD)) {
			this.documentToRender_.emit({
				form: null,
				docxPreviewDocId: null,
				uploadedDocPreviewSourcePath: doc.document.sourceStorageRef,
			});
		} else {
			this.documentToRender_.emit({
				form: null,
				docxPreviewDocId: doc.id,
				uploadedDocPreviewSourcePath: null,
			});
		}
		this.selectedDocumentIds_.set([doc.id]);
	}

	private async setForm(formId: string) {
		const formInstance =
			await this.matterFormService.getMatterFormInstance(formId);

		if (!formInstance) {
			return;
		}
		this.documentToRender_.emit({
			form: formInstance,
			docxPreviewDocId: null,
			uploadedDocPreviewSourcePath: null,
		});
	}

	private onDocumentRemove(doc: MatterWordDocument) {
		if (this.selectedDocumentIds_().some((id) => id === doc.id)) {
			this.onDocumentPreview(
				this.documents_()?.filter((d) => d.id !== doc.id)[0],
			);
		}
		this.RemoveContent({
			packageId: this.packageId_(),
			contentId: doc.id,
		});
	}
}
